import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { requestAll } from '../../../apis/config';
import { instanceApis } from '../../../apis/instanceApis';
import { projectApis } from '../../../apis/projectApis';
import { solrApis } from '../../../apis/solrApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import InstanceStepper from '../../../components/page-sections/instance-stepper/InstanceStepper';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import {
  CLUSTER_TYPES,
  DEFAULT_SUBSCRIPT_NUMBER,
  INSTANCE_CATEGORY,
  INSTANCE_PLANS,
} from '../../../constants';
import { useProject } from '../../../contexts/ProjectContext';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { mapBlobFormData, sentenceCase } from '../../../utils/utils';

const formatRequest = (data) => {
  const {
    plan,
    name,
    description = null,
    icon = null,
    subdomain,
    cidrs,
    solrVersion,
  } = data;

  const instances = [
    {
      type: plan === INSTANCE_CATEGORY.COMMUNITY ? INSTANCE_PLANS.SMALL : plan,
      subscriptNumber: DEFAULT_SUBSCRIPT_NUMBER,
    },
  ];

  const instanceSetProp = {
    solrVersion,
    name,
    description,
    icon,
    subdomain,
    clusterType: CLUSTER_TYPES.SOLR_CLUSTER,
    cidrs,
    instances,
  };

  return instanceSetProp;
};

const NewInstance = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [community, setCommunity] = useState(0);
  const [payment, setPayment] = useState(null);

  const navigate = useNavigate();
  const { projectId } = useParams();
  const { project } = useProject();
  const { showToast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        { data: instancesRes },
        { data: cardsRes },
        { data: billingInfoRes },
        { data: managersRes },
      ] = await requestAll([
        solrApis.getInstances(projectId, {
          category: INSTANCE_CATEGORY.COMMUNITY,
        }),
        projectApis.getProjectCards(projectId),
        projectApis.getProjectBillingInfo(projectId),
        projectApis.getProjectManagers(projectId),
      ]);
      const paymentDetails = {
        cards: cardsRes?.[0],
        billingInfo: billingInfoRes.content?.[0],
        managers: managersRes.content?.[0],
      };
      setPayment(paymentDetails);
      // maximum community instances allowed: 2
      setCommunity(instancesRes.content.length || 0);
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, navigate]);

  const checkDuplicate = async (subdomain) => {
    try {
      return { isValid: await instanceApis.checkSubdomain(subdomain) };
    } catch (e) {
      showToast(
        TOAST_TYPE.ERROR,
        t('unableToCheckSubdomain', { ns: 'notifications' })
      );
      return { isValid: false };
    }
  };

  const onSubmit = async (data) => {
    const instanceSetData = formatRequest(data);
    const formData = mapBlobFormData(instanceSetData, 'instance-set');

    let instance;

    try {
      if (data.plan === INSTANCE_CATEGORY.COMMUNITY) {
        const { data: instanceRes } =
          await solrApis.createCommunitySolrInstance(projectId, formData);
        instance = instanceRes;
      } else {
        const { data: instanceRes } = await solrApis.createStandardSolrInstance(
          projectId,
          formData
        );
        instance = instanceRes;
      }
      const instanceOverview = buildPath({
        path: PRIVATE_ROUTE.INSTANCE_OVERVIEW,
        params: { projectId, instanceId: instance.id },
      });
      setTimeout(() => navigate(instanceOverview), 2000);
      const message = t('createSuccessWithName', {
        ns: 'notifications',
        text: t('instance', { ns: 'instance' }),
        name: instance.name,
      });
      showToast(TOAST_TYPE.SUCCESS, message);
    } catch (error) {
      const message = t('submit', {
        ns: 'errors',
        content: t('instance', { ns: 'instance' }),
      });
      showToast(TOAST_TYPE.ERROR, message);
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('requestedContent', { ns: 'instance' }),
          })
        )}
      />
    );

  return (
    project && (
      <>
        <Breadcrumbs />
        <InstanceStepper
          community={community}
          onSubmit={onSubmit}
          project={project}
          payment={payment}
          onCheckDuplicate={checkDuplicate}
        />
      </>
    )
  );
};

export default NewInstance;
